/**
 * 제휴업체 사업자등록증 자동검증
 *
 * 흐름: 업로드 → CLOVA OCR → 국세청 API → 자동승인/확인필요/자동반려
 *
 * 클라이언트는 상태 enum/판정 헬퍼만 사용. 실제 OCR/NTS 호출은 Cloud Function 측.
 */

// ───── 상태 enum ─────
export const AUTO_VERIFICATION_STATUS = {
  MANUAL_PENDING: 'manual_pending',          // 자동검증 OFF / 수동검토 대기
  OCR_PROCESSING: 'ocr_processing',          // OCR/외부 검증 진행 중
  AUTO_APPROVED: 'ocr_auto_approved',        // 자동승인
  REVIEW_REQUIRED: 'ocr_review_required',    // 관리자 확인 필요
  AUTO_REJECTED: 'ocr_auto_rejected',        // 자동반려
};

export const AUTO_VERIFICATION_LABELS = {
  manual_pending: { label: '수동 대기', color: '#6B7280', bg: '#F3F4F6' },
  ocr_processing: { label: '검증 중', color: '#3B82F6', bg: '#DBEAFE' },
  ocr_auto_approved: { label: '자동승인', color: '#22C55E', bg: '#D1FAE5' },
  ocr_review_required: { label: '확인 필요', color: '#F59E0B', bg: '#FEF3C7' },
  ocr_auto_rejected: { label: '자동반려', color: '#EF4444', bg: '#FEE2E2' },
};

// ───── 사유 코드 ─────
export const VERIFICATION_REASON = {
  AUTO_VERIFY_DISABLED: 'AUTO_VERIFY_DISABLED',
  OCR_CONFIG_MISSING: 'OCR_CONFIG_MISSING',
  OCR_DOCUMENT_INVALID: 'OCR_DOCUMENT_INVALID',
  OCR_CRITICAL_FIELDS_MISSING: 'OCR_CRITICAL_FIELDS_MISSING',
  OCR_LOW_CONFIDENCE: 'OCR_LOW_CONFIDENCE',
  OCR_BUSINESS_TYPE_UNCERTAIN: 'OCR_BUSINESS_TYPE_UNCERTAIN',
  OCR_REPRESENTATIVE_MISMATCH: 'OCR_REPRESENTATIVE_MISMATCH',
  OCR_DUPLICATE_BUSINESS_NUMBER: 'OCR_DUPLICATE_BUSINESS_NUMBER',
  NTS_VALIDATE_FAILED: 'NTS_VALIDATE_FAILED',
  NTS_STATUS_INVALID: 'NTS_STATUS_INVALID',
  EXTERNAL_API_TEMP_ERROR: 'EXTERNAL_API_TEMP_ERROR',
  AUTO_APPROVED: 'AUTO_APPROVED',
  AUTO_CORRECTED_AND_APPROVED: 'AUTO_CORRECTED_AND_APPROVED',
};

export const REASON_TEXT = {
  AUTO_VERIFY_DISABLED: '사업자등록증 자동검증이 꺼져 있어 관리자 수동 검증으로 진행합니다.',
  OCR_CONFIG_MISSING: 'OCR 설정이 누락되어 자동검증을 진행할 수 없습니다.',
  OCR_DOCUMENT_INVALID: '업로드된 문서가 사업자등록증으로 인식되지 않습니다.',
  OCR_CRITICAL_FIELDS_MISSING: 'OCR로 사업자번호/대표자/개업일을 충분히 인식하지 못했습니다.',
  OCR_LOW_CONFIDENCE: 'OCR 신뢰도가 낮아 관리자 확인이 필요합니다.',
  OCR_BUSINESS_TYPE_UNCERTAIN: '사업자유형(개인/법인) 판정이 불확실합니다.',
  OCR_REPRESENTATIVE_MISMATCH: '입력한 대표자명이 사업자등록증 대표자와 일치하지 않습니다.',
  OCR_DUPLICATE_BUSINESS_NUMBER: '동일한 사업자번호로 이미 가입된 계정이 있습니다.',
  NTS_VALIDATE_FAILED: '국세청 진위확인에서 일치하지 않거나 확인 실패했습니다.',
  NTS_STATUS_INVALID: '국세청 상태조회 결과 정상 사업자가 아닙니다.',
  EXTERNAL_API_TEMP_ERROR: '외부 API 일시 오류로 자동검증이 중단되었습니다.',
  AUTO_APPROVED: 'OCR과 국세청 검증을 모두 통과하여 자동승인되었습니다.',
  AUTO_CORRECTED_AND_APPROVED: 'OCR 결과로 입력 정보가 보정된 뒤 자동승인되었습니다.',
};

// ───── 임계값 ─────
export const OCR_CONFIDENCE_REJECT = 0.55;   // 미만 → 자동반려
export const OCR_CONFIDENCE_REVIEW = 0.9;    // 미만 → 확인 필요

// ───── 정규화 helpers ─────
export const normalizeBusinessNumber = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 10 ? digits : null;
};

export const formatBusinessNumber = (value) => {
  const n = normalizeBusinessNumber(value);
  if (!n) return value || '-';
  return `${n.slice(0, 3)}-${n.slice(3, 5)}-${n.slice(5)}`;
};

export const normalizeDateDigits = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 8 ? digits : null;
};

export const formatDateDigits = (value) => {
  const d = normalizeDateDigits(value);
  if (!d) return value || '-';
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
};

export const normalizeName = (value) => String(value ?? '')
  .trim()
  .replace(/\(주\)|주식회사/gi, '')
  .replace(/[^0-9A-Za-z가-힣]/g, '')
  .toLowerCase();

// ───── 라벨 조회 ─────
export const getStatusLabel = (status) =>
  AUTO_VERIFICATION_LABELS[status] || AUTO_VERIFICATION_LABELS.manual_pending;

export const getReasonText = (code) =>
  REASON_TEXT[code] || code || '';
